/**
 * Rate-gate structs — the public (non-iframe) email+captcha unlock flow.
 * P1 of the public-rate-gate feature: shapes only, no behavior (see
 * notes/public-rate-gate-PRD.md + notes/public-rate-gate-structs-P1.md).
 * The unlock/gate actions and BlurCards wiring land in later phases.
 */

/**
 * @readonly
 * @enum {string}
 * @description Lifecycle of the public rate gate, held in `state.route.rateGate.status`.
 * Frozen dict — compare against these members, never raw strings.
 */
export const RateGateStatus = Object.freeze({
  /** No unlock attempted yet (initial; blur cards show the email form). */
  IDLE: 'idle',
  /** Unlock request in flight (captcha + server check). */
  VERIFYING: 'verifying',
  /** Email accepted — rates revealed, lead created; discount may be present. */
  UNLOCKED: 'unlocked',
  /** Hard gate — email/domain belongs to an existing account; show log-in card. */
  GATED: 'gated',
  /** Unlock failed (network/captcha/server) — inline retryable error. */
  ERROR: 'error',
});

/**
 * @description Initial shape of `state.route.rateGate`. `discount` from an unlock
 * response is NOT stored here — it reuses the existing `state.route.discount`
 * ({ id, code, percent, expiresAt }; existing consumers read only `.id`).
 * `leadId` (from a 200 unlock) rides the slice so the public booking payload can
 * carry `crm.leadId`; `loginUrl` (from a 409 gate) feeds the hard-gate [Log in].
 * The reducer MERGES SET_RATE_GATE payloads, so partial updates are safe.
 */
export const RATE_GATE_INITIAL = Object.freeze({
  status: RateGateStatus.IDLE,
  errorMessage: null,
  leadId: null,
  loginUrl: null,
});

/**
 * Customer-facing retryable error copy (signed-off copy sheet,
 * notes/public-rate-gate-mockup.html §5). Shared by the unlock thunk (which
 * stores it in `rateGate.errorMessage`) and the gate card (which renders it).
 */
export const RATE_GATE_ERROR_COPY =
  'We couldn’t verify that just now — please try again.';
